import { Input } from "@/components/ui/input";
import { entityHue } from "@/components/workspace/OrgEntitySwitcher";
import { useOrganizationStore } from "@/lib/organization";
import { useSlashFocus } from "@/lib/use-slash-focus";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/lib/workspace";
import { Link } from "@tanstack/react-router";
import {
	Bell,
	BookOpen,
	Bot,
	Boxes,
	Building2,
	CheckSquare,
	ClipboardCheck,
	CreditCard,
	FileSpreadsheet,
	FlaskConical,
	GitBranch,
	Globe,
	Hammer,
	Key,
	Mail,
	Package,
	Palette,
	Plug,
	Puzzle,
	Search,
	Settings2,
	Shield,
	Tag as TagIcon,
	Users,
	Webhook,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface SubPage {
	to: string;
	label: string;
	keywords?: string;
}

interface NavItem {
	to: string;
	label: string;
	icon: LucideIcon;
	/** Search keywords (not displayed, only for filtering). */
	keywords?: string;
	/** If set, only show when workspace has this feature enabled. */
	feature?: string;
	/** Nested sub-pages (shown only when searching). */
	children?: SubPage[];
}

interface NavGroup {
	group: string;
	items: NavItem[];
}

export function getSettingsNav(t: (key: string) => string): NavGroup[] {
	return [
		{
			group: t("sidebar_group_organization"),
			items: [
				{
					to: "/settings/organization",
					label: t("sidebar_organization"),
					icon: Building2,
					keywords: t("sidebar_kw_organization"),
				},
				{
					to: "/settings/subscription",
					label: t("sidebar_subscription"),
					icon: CreditCard,
					keywords: t("sidebar_kw_subscription"),
				},
			],
		},
		{
			group: t("sidebar_group_entity"),
			items: [
				{
					to: "/settings/general",
					label: t("sidebar_general"),
					icon: Settings2,
					keywords: t("sidebar_kw_general"),
				},
				{
					to: "/settings/notifications",
					label: t("sidebar_notifications"),
					icon: Bell,
					keywords: t("sidebar_kw_notifications"),
				},
				{
					to: "/settings/appearance",
					label: t("sidebar_appearance"),
					icon: Palette,
					keywords: t("sidebar_kw_appearance"),
				},
			],
		},
		{
			group: t("sidebar_group_team"),
			items: [
				{
					to: "/settings/members",
					label: t("sidebar_members"),
					icon: Users,
					keywords: t("sidebar_kw_members"),
				},
				{
					to: "/settings/roles",
					label: t("sidebar_roles"),
					icon: Shield,
					keywords: t("sidebar_kw_roles"),
				},
			],
		},
		{
			group: t("sidebar_group_data"),
			items: [
				{
					to: "/settings/catalog",
					label: t("sidebar_catalog"),
					icon: Package,
					keywords: t("sidebar_kw_catalog"),
					children: [
						{
							to: "/settings/catalog/search",
							label: t("sidebar_catalog_search"),
							keywords: t("sidebar_kw_catalog_search"),
						},
						{
							to: "/settings/catalog/enrichment",
							label: t("sidebar_catalog_enrichment"),
							keywords: t("sidebar_kw_catalog_enrichment"),
						},
						{
							to: "/settings/catalog/synonyms",
							label: t("sidebar_catalog_synonyms"),
							keywords: t("sidebar_kw_catalog_synonyms"),
						},
						{
							to: "/settings/catalog/migration",
							label: t("sidebar_catalog_migration"),
							keywords: t("sidebar_kw_catalog_migration"),
						},
						{
							to: "/settings/catalog/groups",
							label: "Группы атрибутов",
							keywords: "группы атрибуты свойства property groups",
						},
						{
							to: "/settings/catalog/completeness",
							label: "Заполненность",
							keywords: "заполненность completeness quality",
						},
					],
				},
				{
					to: "/settings/tags",
					label: t("sidebar_tags"),
					icon: TagIcon,
					keywords: t("sidebar_kw_tags"),
				},
				{
					to: "/settings/export-templates",
					label: t("sidebar_export_templates"),
					icon: FileSpreadsheet,
					keywords: t("sidebar_kw_export_templates"),
				},
			],
		},
		{
			group: t("sidebar_group_agent"),
			items: [
				{
					to: "/settings/knowledge",
					label: t("sidebar_knowledge"),
					icon: BookOpen,
					keywords: t("sidebar_kw_knowledge"),
					feature: "agent",
				},
				{
					to: "/settings/integrations",
					label: t("sidebar_integrations"),
					icon: Plug,
					keywords: t("sidebar_kw_integrations"),
					feature: "agent",
				},
				{
					to: "/settings/agent-profiles",
					label: "Профили агентов",
					icon: Bot,
					keywords: "профили агент шаблон template profile",
					feature: "agent",
				},
				{
					to: "/settings/code-templates",
					label: "Шаблоны кода",
					icon: Boxes,
					keywords: "шаблоны код template code git",
					feature: "agent",
				},
			],
		},
		{
			group: t("sidebar_group_modules"),
			items: [
				{
					to: "/settings/compliance",
					label: t("sidebar_compliance"),
					icon: ClipboardCheck,
					keywords: t("sidebar_kw_compliance"),
					feature: "compliance",
				},
				{
					to: "/settings/metal",
					label: t("sidebar_metal"),
					icon: Hammer,
					keywords: t("sidebar_kw_metal"),
					feature: "metal",
				},
				{
					to: "/settings/work-items",
					label: t("sidebar_work_items"),
					icon: CheckSquare,
					keywords: t("sidebar_kw_work_items"),
					feature: "crm",
				},
				{
					to: "/settings/team-email",
					label: t("sidebar_team_email"),
					icon: Mail,
					keywords: t("sidebar_kw_team_email"),
				},
			],
		},
		{
			group: t("sidebar_group_extensions"),
			items: [
				{
					to: "/settings/plugins",
					label: t("sidebar_plugins"),
					icon: Puzzle,
					keywords: t("sidebar_kw_plugins"),
					children: [
						{
							to: "/settings/plugins",
							label: t("sidebar_plugins_installed"),
							keywords: t("sidebar_kw_plugins_installed"),
						},
						{
							to: "/settings/plugins/marketplace",
							label: t("sidebar_plugins_marketplace"),
							keywords: t("sidebar_kw_plugins_marketplace"),
						},
						{
							to: "/settings/plugins/developer",
							label: t("sidebar_plugins_developer"),
							keywords: t("sidebar_kw_plugins_developer"),
						},
					],
				},
				{
					to: "/settings/diagram",
					label: t("sidebar_diagram"),
					icon: GitBranch,
					keywords: t("sidebar_kw_diagram"),
				},
			],
		},
		{
			group: t("sidebar_group_integration"),
			items: [
				{
					to: "/portals",
					label: t("sidebar_portal"),
					icon: Globe,
					keywords: t("sidebar_kw_portal"),
					feature: "portal",
				},
			],
		},
		{
			group: t("sidebar_group_development"),
			items: [
				{
					to: "/settings/tokens",
					label: t("sidebar_tokens"),
					icon: Key,
					keywords: t("sidebar_kw_tokens"),
				},
				{
					to: "/settings/service-accounts",
					label: t("sidebar_service_accounts"),
					icon: Bot,
					keywords: t("sidebar_kw_service_accounts"),
				},
				{
					to: "/settings/eval-datasets",
					label: t("sidebar_eval_datasets"),
					icon: FlaskConical,
					keywords: t("sidebar_kw_eval_datasets"),
				},
				{
					to: "/settings/webhooks",
					label: t("sidebar_webhooks"),
					icon: Webhook,
					keywords: t("sidebar_kw_webhooks"),
				},
			],
		},
	];
}

export function SettingsSidebar() {
	const { t } = useTranslation("settings");
	const SETTINGS_NAV = getSettingsNav(t);
	const ws = useWorkspaceStore((s) => s.selected());
	const org = useOrganizationStore((s) => s.selected());
	const features = ws?.features ?? [];
	const [query, setQuery] = useState("");

	const searchRef = useRef<HTMLInputElement>(null);
	const clearQuery = useCallback(() => setQuery(""), []);
	useSlashFocus(searchRef, clearQuery);

	const isSearching = query.trim().length > 0;
	const normalizedQuery = query.trim().toLowerCase();

	const filteredNav = useMemo(() => {
		if (!isSearching) return SETTINGS_NAV;
		const words = normalizedQuery.split(/\s+/);

		return SETTINGS_NAV.map((group) => ({
			...group,
			items: group.items.filter((item) => {
				const childrenKeywords = (item.children ?? [])
					.map((c) => `${c.label} ${c.keywords ?? ""}`)
					.join(" ");
				const haystack =
					`${item.label} ${item.keywords ?? ""} ${group.group} ${childrenKeywords}`.toLowerCase();
				return words.every((word) => haystack.includes(word));
			}),
		})).filter((group) => group.items.length > 0);
	}, [isSearching, normalizedQuery]);

	const matchedChildren = useMemo(() => {
		if (!isSearching) return new Map<string, SubPage[]>();
		const words = normalizedQuery.split(/\s+/);
		const result = new Map<string, SubPage[]>();

		for (const group of filteredNav) {
			for (const item of group.items) {
				if (!item.children) continue;
				const matched = item.children.filter((c) => {
					const hay = `${c.label} ${c.keywords ?? ""}`.toLowerCase();
					return words.some((w) => hay.includes(w));
				});
				if (matched.length > 0) {
					result.set(item.to, matched);
				}
			}
		}
		return result;
	}, [isSearching, normalizedQuery, filteredNav]);

	return (
		<aside className="flex w-56 shrink-0 flex-col border-r bg-muted/40">
			<div className="px-2 pt-3 pb-2">
				<div className="relative">
					<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
					<Input
						ref={searchRef}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={t("sidebar_search_placeholder")}
						className="h-8 pl-8 text-sm bg-background"
					/>
				</div>
			</div>

			<nav className="flex-1 space-y-1 overflow-y-auto px-2 pb-3">
				{filteredNav.map((group) => {
					const visibleItems = group.items.filter(
						(item) => !item.feature || features.includes(item.feature),
					);
					if (visibleItems.length === 0) return null;

					const isOrgGroup = group.group === t("sidebar_group_organization");
					const isEntityGroup = group.group === t("sidebar_group_entity");

					return (
						<div key={group.group}>
							{isOrgGroup && org && !isSearching ? (
								<div className="flex items-center gap-2 rounded-md px-2.5 py-2 mb-0.5">
									<Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
									<span className="text-[11px] font-medium text-muted-foreground/60 uppercase tracking-wider truncate">
										{org.name}
									</span>
								</div>
							) : isEntityGroup && !isSearching ? (
								<>
									<div className="mx-2 my-2 border-t" />
									<div className="flex items-center gap-2 rounded-md px-2.5 py-2 mb-0.5">
										{ws?.avatarUrl ? (
											<img
												src={ws.avatarUrl}
												alt={ws.name}
												className="h-3.5 w-3.5 rounded-sm object-cover"
											/>
										) : ws ? (
											<div
												className="flex h-3.5 w-3.5 items-center justify-center rounded-sm text-[7px] font-bold text-white"
												style={{
													backgroundColor: `hsl(${entityHue(ws.name)}, 55%, 45%)`,
												}}
											>
												{ws.name.charAt(0).toUpperCase()}
											</div>
										) : null}
										<span className="text-[11px] font-medium text-muted-foreground/60 uppercase tracking-wider truncate">
											{ws?.name ?? t("sidebar_group_entity")}
										</span>
									</div>
								</>
							) : (
								<div
									className={cn(
										"flex w-full items-center rounded-md px-2.5 py-1.5",
										"text-[11px] font-medium text-muted-foreground/60 uppercase tracking-wider",
									)}
								>
									{group.group}
								</div>
							)}

							{
								<div className="space-y-0.5 pb-1">
									{visibleItems.map((item) => {
										const children = isSearching
											? matchedChildren.get(item.to)
											: undefined;
										return (
											<div key={item.to}>
												<Link
													to={item.to}
													className={cn(
														"flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium",
														"text-muted-foreground transition-colors",
														"hover:bg-accent hover:text-accent-foreground",
														"[&.active]:bg-accent [&.active]:text-accent-foreground",
													)}
												>
													<item.icon className="h-4 w-4 shrink-0" />
													<span className="truncate">{item.label}</span>
												</Link>
												{children?.map((child) => (
													<Link
														key={child.to}
														to={child.to}
														className={cn(
															"flex items-center gap-2 rounded-md pl-9 pr-2.5 py-1 text-xs",
															"text-muted-foreground transition-colors",
															"hover:bg-accent hover:text-accent-foreground",
															"[&.active]:bg-accent [&.active]:text-accent-foreground",
														)}
													>
														<span className="truncate">{child.label}</span>
													</Link>
												))}
											</div>
										);
									})}
								</div>
							}
						</div>
					);
				})}

				{isSearching && filteredNav.length === 0 && (
					<p className="px-2.5 py-4 text-sm text-muted-foreground text-center">
						{t("sidebar_nothing_found")}
					</p>
				)}
			</nav>
		</aside>
	);
}
